import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { ApolloProvider, ApolloConsumer, gql } from '@apollo/client';
import { Subscription } from '@apollo/client/react/components';
import PropTypes from 'prop-types';
import MapContainer from "./MapContainer";
import client from '../apollo';
import { GPS_UPDATE_INTERVAL_MS, MAX_GAP_DURATION_MS, MIN_GAP_DURATION_MS } from '../constants';
import { mapMatch, detectGapMs, fillGap, scoreMatchConfidence } from '../services/valhalla';
import { createSegment, calculateRealisticDuration } from '../physics/vehiclePhysics';
import './App.css';

const LOCATION_SUBSCRIPTION = gql`
  subscription getLocation($vehicleId: String!) {
    api_vehicle_location(
      where: { vehicle_id: { _eq: $vehicleId } }
      order_by: { timestamp: desc }
      limit: 1
    ) {
      location
      speed_mph
      heading_degrees
      timestamp
    }
  }
`;

// ── Target FPS ───────────────────────────────────────────────────────────────
const TARGET_FPS = 60;
const FRAME_INTERVAL = 1000 / TARGET_FPS;

function App(props) {
  const [vehicleId, setVehicleId] = useState(props.vehicleId);
  const [Location, setLocation] = useState(null);

  const latestLocation = useRef(null);
  const positionQueue = useRef([]);
  const isAnimating = useRef(false);
  const firstUpdate = useRef(true);

  // Cache for Valhalla road matches to avoid repeated calls for same segments
  const roadMatchCache = useRef(new Map());

  useEffect(() => {
    setVehicleId(props.vehicleId);
  }, [props.vehicleId]);

  // ── Valhalla map-matching with caching ───────────────────────────────────

  const getMatchedRoad = useCallback(async (startPt, endPt) => {
    const cacheKey = `${startPt.lat},${startPt.lng}-${endPt.lat},${endPt.lng}`;
    if (roadMatchCache.current.has(cacheKey)) {
      return roadMatchCache.current.get(cacheKey);
    }

    const result = await mapMatch([startPt, endPt], {
      gpsAccuracy: 20,
      headingTolerance: 45,
    });

    if (result && result.roadShape) {
      // Keep cache bounded
      if (roadMatchCache.current.size > 100) {
        const firstKey = roadMatchCache.current.keys().next().value;
        roadMatchCache.current.delete(firstKey);
      }
      roadMatchCache.current.set(cacheKey, result);
    }

    return result;
  }, []);

  // ── Gap Detection & Filling ──────────────────────────────────────────────
  // Time-based: handles gaps from 6s up to 5 minutes.
  // One Valhalla call per gap → dense road-following waypoints.
  // Short gaps (<30s) use trace_route, long gaps use route endpoint.

  const fillGaps = useCallback(async (prevPoint, newPoint) => {
    const gapMs = detectGapMs(prevPoint, newPoint, MIN_GAP_DURATION_MS);

    if (gapMs === 0 || gapMs > MAX_GAP_DURATION_MS) {
      // No gap, or gap exceeds max (>5 min) — don't fill
      return [];
    }

    // fillGap makes ONE Valhalla call, generates waypoints at GPS_UPDATE_INTERVAL_MS spacing
    // e.g., 5 min gap at 3s intervals → ~99 intermediate road-following waypoints
    return await fillGap(prevPoint, newPoint, gapMs, GPS_UPDATE_INTERVAL_MS);
  }, []);

  // ── Core Animation ───────────────────────────────────────────────────────

  const animationStartRef = useRef(null);

  const animate = useCallback(async () => {
    if (isAnimating.current) return;

    const start = positionQueue.current[0];
    const end = positionQueue.current[1];
    if (!start || !end) {
      isAnimating.current = false;
      return;
    }

    isAnimating.current = true;

    // Try to get road geometry from Valhalla for this segment
    let roadPath = null;
    let confidence = 0;
    try {
      const matchResult = await getMatchedRoad(start, end);
      if (matchResult && matchResult.roadShape) {
        // Score how well the Valhalla match fits the GPS evidence.
        // Low confidence = vehicle took a turn Valhalla didn't expect
        // (parking lot, U-turn, side street, off-road).
        confidence = scoreMatchConfidence(matchResult, start, end);
        roadPath = matchResult.roadShape;
      }
    } catch (err) {
      // Valhalla unavailable — continue with straight-line physics
    }

    // Create a physics-based animation segment
    // confidence flows through to:
    //   - position: <0.3 = straight line, 0.3–0.7 = blended, >0.7 = follow road
    //   - heading: blends GPS heading (always truth) with road heading by confidence
    const queueDepth = positionQueue.current.length;
    const latestTs = queueDepth > 2
      ? positionQueue.current[queueDepth - 1].timestamp
      : null;
    const segmentDuration = calculateRealisticDuration(start, end, queueDepth, latestTs);
    const segment = createSegment(start, end, roadPath, { confidence });

    animationStartRef.current = Date.now();
    let lastDrawTime = -1;

    const animationLoop = (timestamp) => {
      if (timestamp < lastDrawTime + FRAME_INTERVAL) {
        requestAnimationFrame(animationLoop);
        return;
      }
      lastDrawTime = timestamp;

      const elapsed = Date.now() - animationStartRef.current;
      const progress = elapsed / segmentDuration;

      if (progress < 1) {
        // Get vehicle state from the physics engine
        const state = segment.at(Math.min(progress, 1));

        // Only update if position actually changed (avoid redundant renders)
        if (Math.abs(start.lat - state.lat) > 0.000001 ||
            Math.abs(start.lng - state.lng) > 0.000001) {
          setLocation({
            lat: state.lat,
            lng: state.lng,
            speed: state.speed,
            heading: state.heading,
            timestamp: state.timestamp,
          });
        }
        requestAnimationFrame(animationLoop);
      } else {
        // Segment complete — snap to end position
        setLocation({
          lat: end.lat,
          lng: end.lng,
          speed: end.speed,
          heading: end.heading,
          timestamp: end.timestamp,
        });

        isAnimating.current = false;

        // Advance the queue and animate the next segment
        if (positionQueue.current.length > 0) {
          positionQueue.current.shift();
          animate();
        }
      }
    };

    requestAnimationFrame(animationLoop);
  }, [getMatchedRoad]);

  // ── Subscription Handler ─────────────────────────────────────────────────

  const handleSubscriptionData = useCallback(async (data) => {
    if (!data || !data.api_vehicle_location || data.api_vehicle_location.length === 0) {
      return;
    }

    const locationData = data.api_vehicle_location[0];
    if (!locationData || !locationData.location) return;

    const [lat, lng] = locationData.location
      .slice(1, -1)
      .split(',')
      .map(str => Number(str.trim()));

    const newLocation = {
      lat,
      lng,
      speed: locationData.speed_mph,
      heading: locationData.heading_degrees,
      timestamp: new Date(locationData.timestamp).getTime(),
    };

    // Deduplicate
    const isDuplicate = positionQueue.current.some(
      p => p.lat === newLocation.lat &&
           p.lng === newLocation.lng &&
           p.timestamp === newLocation.timestamp
    );
    if (isDuplicate) return;

    // Gap detection: if there's a gap, fill with Valhalla road-snapped waypoints
    const lastInQueue = positionQueue.current[positionQueue.current.length - 1];
    if (lastInQueue) {
      try {
        const filledPoints = await fillGaps(lastInQueue, newLocation);
        for (const fp of filledPoints) {
          positionQueue.current.push(fp);
        }
      } catch (err) {
        // Gap fill failed — proceed without intermediate points
      }
    }

    latestLocation.current = newLocation;
    positionQueue.current.push(newLocation);

    // First update: render immediately so the map has a position
    if (firstUpdate.current) {
      setLocation(newLocation);
      firstUpdate.current = false;
    }

    // Start animating if we have at least 2 points
    if (positionQueue.current.length > 1) {
      animate();
    }
  }, [animate, fillGaps]);

  // ── Render ───────────────────────────────────────────────────────────────

  const vehicleLocation = {
    width: '100%',
    marginBottom: '20px',
  };

  const marker_location = useMemo(() => {
    if (Location) {
      return { lat: Location.lat, lng: Location.lng };
    }
    return null;
  }, [Location]);

  return (
    <ApolloConsumer>
      {client => (
        <Subscription
          subscription={LOCATION_SUBSCRIPTION}
          variables={{ vehicleId }}
          onSubscriptionData={({ subscriptionData }) =>
            handleSubscriptionData(subscriptionData.data)
          }
        >
          {({ loading, error }) => {
            if (loading) return <p>Loading...</p>;
            if (error) return <p>Error!</p>;
            return (
              <div style={vehicleLocation}>
                <div className="row">
                  <div className="col-md-12 col-xs-12">
                    <h4>Live Tracking</h4>
                    <div className="tracking_info">
                      Location will be updated continuously up to shared link expiration
                    </div>
                    <div className="map_wrapper">
                      {marker_location && (
                        <MapContainer
                          marker_location={marker_location}
                          heading={Location.heading}
                          speed={Location.speed}
                          timestamp={Location.timestamp}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          }}
        </Subscription>
      )}
    </ApolloConsumer>
  );
}

App.propTypes = {
  vehicleId: PropTypes.string.isRequired,
};

const ApolloWrappedComponent = (props) => {
  return (
    <ApolloProvider client={client}>
      <App {...props} />
    </ApolloProvider>
  );
};

export default ApolloWrappedComponent;
